import { Injectable } from '@angular/core';
import { AppInjector } from '../../app-injector';
import { IntegrationItemServiceInf, IntegrationProductFilterInf, StatusString, UserLicense } from '../interfaces/integrations';
import { mitelOneLicenses } from '../constants/constants';
import { Statement } from '@mitel/cloudlink-sdk/admin';
import { MitelOneConfigDialogComponent } from '../../modules/integrations-applications/components/integ-config-dialogs/mitel-one-config-dialog/mitel-one-config-dialog.component';
import { AdminService } from 'src/app/core/services/admin.service';
import { UsersBulkEditMitelOneService } from 'src/app/core/services/users-bulk-edit-mitel-one.service';
import { IntegPolicyService } from 'src/app/core/services/integ-policy.service';
import { Account, User } from '@mitel/cloudlink-sdk';
import { AccountsService } from './accounts.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogs } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum';
import { OrdersService } from './orders.service';
import { SpinnerService } from './spinner.service';
import { RBACAuthGuardService } from '../guards/rbac-auth-guard.service';
import { ActionType, CategoryId } from '../utils/rbac.util';
import { UserPreFilterReasons } from '../constants/user-filter-constants';
import { UserMitelOneSharedService } from './user-mitel-one-shared.service';
import { XLaunchService } from 'src/app/core/services/x-launch.service';
import { environment } from '../../../environments/environment';
import { StatusType } from '../constants/statusType.enum';
import { Util } from '../utils/Util';

const DisableEditAvatarNameStatement: Statement =  {
	statementId: 'avatar-name-controls',
	action: [
		"*"
	],
	resource: [
		"https://mitel.io/auth/mitel-one/features/change-avatar"
	],
	effect: "Deny" as const
}

const MitelOneBetaStatement: Statement = {
	statementId: 'mitel-one-beta-access',
	action: [
		'*'
	],
	resource: [

		"https://mitel.io/auth/moma/features/mitel-one-beta-access"
	],
	effect: 'Allow' as const
}

@Injectable({
	providedIn: 'root'
})

export class IntegMitelOneService implements IntegrationItemServiceInf, IntegrationProductFilterInf {

	private adminService: AdminService;
	private policyService: IntegPolicyService;
	private accountsService: AccountsService;
	private mitelOneSharedSvc: UserMitelOneSharedService;
	public dialog: MatDialog;
	private orderService: OrdersService;
	private spinnerService: SpinnerService;
	private rbacAuthGuardService: RBACAuthGuardService;
	private usersBulkEditMitelOneService: UsersBulkEditMitelOneService;
	private xlaunchService: XLaunchService = undefined;

	constructor(  ) 
	{
	}

	initServices()
	{
		if ( !this.adminService )
			this.adminService = AppInjector.injector.get(AdminService);

		if ( !this.policyService )
			this.policyService = AppInjector.injector.get(IntegPolicyService);

		if ( !this.accountsService )
			this.accountsService = AppInjector.injector.get(AccountsService);

		if ( !this.dialog )
			this.dialog = AppInjector.injector.get(MatDialog);

		if ( !this.orderService )
			this.orderService = AppInjector.injector.get(OrdersService);
			
		if ( !this.spinnerService )
			this.spinnerService = AppInjector.injector.get(SpinnerService);
		
		if ( !this.rbacAuthGuardService )
			this.rbacAuthGuardService = AppInjector.injector.get(RBACAuthGuardService);

		if ( !this.usersBulkEditMitelOneService )
			this.usersBulkEditMitelOneService = AppInjector.injector.get(UsersBulkEditMitelOneService);
			
		if ( !this.mitelOneSharedSvc )
			this.mitelOneSharedSvc = AppInjector.injector.get(UserMitelOneSharedService);
	
		if ( !this.xlaunchService )
			this.xlaunchService = AppInjector.injector.get(XLaunchService);
	}
	
	canBeSelected(account: Account): Promise<boolean>
	{
		return Promise.resolve(true);
	}
	
	getConfigurationDialog() : any
	{
		return MitelOneConfigDialogComponent;
	}

	isSetupComplete(account: Account) : Promise<boolean>
	{
		return Promise.resolve(true);
	}

	handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[]): Promise<any>
	{
		this.initServices();
		return this.adminService.getAccountWithoutFrills(account.accountId).then(async newAccount => {
			if ( state == false )
			{
				this.spinnerService.showSpinner();
				this.spinnerService.setSpinnerMessageByKey('spinnerMessages.loading_wait');

				// update mitel one beta policy
				await this.updateMitelOnePolicy(true, MitelOneBetaStatement, account, newAccount);

				// remove the policy statement for the avatar/ name
				return this.saveMitelOneConfig( account, { allowAvatarChange: true } ).then( ()=> {
					return this.adminService.getUsersByAccountIdWithoutSpinner(account.accountId, {$Expand: 'tags'}).then(users=>{

						//checks if there is mitel one subscription, if yes then licenses are assigned to users or not
						return this.mitelOneSharedSvc.getRemainingLicenseDetails().then( licenses => {
							this.spinnerService.hideSpinner();
							if(Object.keys(licenses).length > 0){
								 let assignedUserLicenses : boolean;
								 Object.keys(licenses).forEach( license => {
									if(licenses[license].used != 0){
										assignedUserLicenses = true;
									}
								});

								//if there are users with the licenses assigned, then remove the licenses
								if(assignedUserLicenses){
									// now remove all the licenses from all users
									return this.usersBulkEditMitelOneService.saveUserMitelOneBulkOperation(account.accountId, users, {license: "unassignAll"}).then(result => {
										this.orderService.remainingLicensesChanged.next(); // need the license display to update
										return Promise.resolve();
									});
								}

							}
							return Promise.resolve();
						});
						
					})
				})
			}
			else
			{
				return this.policyService.addStatementToPolicy(newAccount, MitelOneBetaStatement).then(policy => {
					if (newAccount['policyId'] == undefined) {
						let dataChanges = {
							policyId: policy.policyId,
						}
						return this.adminService.updateAccount(newAccount.accountId, dataChanges).then(result => {
							this.accountsService.mergeAccountWithModel(result);
							return Promise.resolve();
						});
					} 
					else {
						return Promise.resolve();
					}
				}).catch(err => {
					console.error("Failed to add/update policy", err);
					throw err;
				});
			}
		});
	}

	async getStatusString(account: Account): Promise<StatusString>
	{
		this.initServices();
		let status: StatusString = {
			status: 'integrations.mitel-one.no-subscription',
			type: StatusType.caution
		};

		try
		{
			// as a transition feature, a Mitel One integration does not need a sub if there is 
			// an onboarded 400
			let tags = await this.accountsService.getAccountTagsById( account.accountId );
			if ( Util.doesAccountHaveMiVoice400Onboarded(tags) == true )
			{
				return undefined;
			}
		}
		catch (err) 
		{
			// continue with the testing below
			// this really should not happen but is harmless
			console.log("requesting tags failed? it should not have", err)
		}

		// check to see if there is a mitel One sub for the account
		// If no, then show a caution that a subscription is needed

		return this.orderService.doesCustomerAccountHaveExistingProductSubs(environment.subscriptionIds.MitelOne).then(existingSubs => {
			if ( existingSubs === true )
			{
				return undefined;
			}
			else
			{
				return status;
			}
		})
		.catch( err => {
			console.log("Could not retrieve subscriptions for the account, assume none", err);
			return status;
		})
	}

	getConfigButtonLabel(account: Account): Promise<string>
	{
		return undefined;
	}

	isButtonDisabled(account: Account): Promise<boolean>
	{
		return Promise.resolve(false);
	}

	canDeleteIntegration(account: Account): Promise<boolean>
	{
		this.initServices();
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.REMOVE_MITELONE_CONFIRMATION } });
		return dialogRef.afterClosed().toPromise().then(result => {
			return result;
		});

	}

	getMitelOneConfiguration(account: Account): Promise<any>
	{
		this.initServices();

		let config = {
			allowAvatarChange: true
		};

		return this.adminService.getAccountWithoutFrills(account.accountId).then( newAccount => {
			if ( newAccount['policyId'] != undefined )
			{
				return this.adminService.getPolicy(account.accountId, newAccount['policyId']).then( policy => {
					console.log("Here are the account policies", policy);
					const foundPolicy = policy.statements.find(stmt => {
						return stmt.statementId === DisableEditAvatarNameStatement.statementId;
					});
					if ( foundPolicy )
					{
						config.allowAvatarChange = false; // the policy being present means it is not allows
					}
					return config;
				}) 
				.catch(err => {
					console.log("Here is the error from getting policies", err)
					throw err;
				});
			}
			else
				return config;
		}).catch(err => {
			console.log("Failed to get account", account,  err);
			throw err;
		});
	}

	saveMitelOneConfig( account: Account, config: any ): Promise<any>
	{
		this.initServices();
		// it has been proven that other feature are changing the policy and the account passed in might not have the 
		// accuarate data at the moment

		return this.adminService.getAccountWithoutFrills(account.accountId).then( newAccount => {
			this.updateMitelOnePolicy(config.allowAvatarChange, DisableEditAvatarNameStatement, account, newAccount);
		}).catch(err => {
			console.log("Failed to get the account", err);
			throw err;
		});
	}

	updateMitelOnePolicy(configProperty, policyStatement, account, newAccount) : Promise<any> {
		if (configProperty == true) {
			// remove the account policy statement
			if (newAccount['policyId'] === undefined) // nothing to remove
			{
				return Promise.resolve({});
			}
			return this.adminService.getPolicy(account.accountId, newAccount['policyId']).then(policy => {
				console.log("Here are the account policies", policy);
				const foundPolicy = policy.statements.find(stmt => {
					return stmt.statementId === policyStatement.statementId;
				});
				if (foundPolicy) {
					return this.policyService.removeStatementFromPolicy(newAccount, policy, policyStatement);
				}
				return Promise.resolve({});
			});

		} else {
			// add the policy statement for the account
			return this.policyService.addStatementToPolicy(newAccount, policyStatement).then(policy => {
				if (newAccount['policyId'] == undefined) {
					let dataChanges = {
						policyId: policy.policyId,
					}
					return this.adminService.updateAccount(newAccount.accountId, dataChanges).then(result => {
						this.accountsService.mergeAccountWithModel(result);
						return policy;
					});
				}
				else {
					return policy;
				}
			}).catch(err => {
				console.log("Failed to add/update policy", err);
				throw err;
			});
		}
	}

	isIntegrationStateChangeable(account: Account): Promise<boolean> {
		this.initServices();

		let isDenied = this.rbacAuthGuardService.isPermissionDeniedForUser(CategoryId.INTEGRATIONMITELONE, ActionType.DELETE);
		return Promise.resolve(!isDenied);
	}

	getUserLicense( user: User, account: Account ): Promise<UserLicense>
	{
		this.initServices();
		return this.orderService.getUsersLicensesFromCachedList(user.userId).then( licenses => {

			let userLicense: UserLicense;
			for ( var i = 0; i < licenses.length; i++ )
			{
				if(mitelOneLicenses.includes(licenses[i])){
					userLicense = {
						code: licenses[i],
						displayStringKey: 'product_licenses.mitelOne.' + licenses[i]
					}
					return userLicense;
				}  
			}
			return undefined;
		});
	}

	canFilterUsersOnProduct(account: Account): boolean
	{
		return true;
	}

	canPreFilterForCondition( account: Account, reason: UserPreFilterReasons ): boolean
	{
		if ( reason == UserPreFilterReasons.XLAUNCH )
			return true;
		else
			return false;
	}

	async filterUsersBasedOnProduct(account: Account, users: User[], filter: any[] ): Promise<User[]>
	{
		this.initServices();
		let newUsersList = new Array<User>();
	
		for ( var i = 0; i < users.length; i++ )
		{
			try
			{
				let licenses = await this.orderService.getUsersLicensesFromCachedList(users[i].userId);
				for ( var j = 0; j < licenses.length; j++ )
				{
					if(mitelOneLicenses.includes(licenses[j]))
					{
						newUsersList.push(users[i]);
					}  
				}

			}
			catch(e)
			{
				console.log("Is there an issue here?", e)
			}
		}
		return newUsersList;
	
	}

	getInfoLink(account: Account): string
	{
		this.initServices();
		if ( this.xlaunchService.isXLaunchedFromBoss() )
		{
			return "integrations.xlaunch-help.mitel-one";
		}
		return undefined;
	}
}
